import zod from 'zod'
import { Event, EventStatus, UpdateEvent } from '@wellesley/openapi'
import { logger } from 'helpers/logger'
import { EVENT_PRIVACY_KEYS, EventPrivacy } from '../../helpers/Events.utils'
import {
  combineDateAndTime,
  getStartOfNextDay,
  getTimeString,
  toISONoMs,
} from '../../helpers/dates/Event.dates'

const statusOptions = [
  'CONFIRMED',
  'CANCELLED',
] as const satisfies readonly EventStatus[]

const DATE_ERROR_MSG = 'Pick a date'

const TIME_REGEX = /^\d{2}:\d{2}$/
const TIME_ERROR_MSG = 'Pick a time'

export const maxEventNameLength = 512
export const maxEventDescriptionLength = 5000

const eventCommonFieldsSchema = zod.object({
  name: zod
    .string()
    .min(1, { message: 'Event name is required' })
    .max(maxEventNameLength, {
      message: `Event name must be at most ${maxEventNameLength} characters`,
    }),

  description: zod
    .string()
    .max(maxEventDescriptionLength, {
      message: `Description must be at most ${maxEventDescriptionLength} characters`,
    })
    .nullable()
    .optional(),

  startDate: zod.preprocess(
    (val) => (!val ? undefined : val),
    zod.date({ required_error: DATE_ERROR_MSG }),
  ),
  startTime: zod.preprocess(
    (val) => (!val ? undefined : val),
    zod
      .string({ required_error: TIME_ERROR_MSG })
      .regex(TIME_REGEX, { message: TIME_ERROR_MSG }),
  ),
  endDate: zod.date().nullable(),
  endTime: zod
    .string()
    .regex(TIME_REGEX, { message: TIME_ERROR_MSG })
    .nullable(),
  _includeEndDate: zod.boolean(),

  ianaTimeZone: zod.string().min(1, { message: 'Time zone must be selected' }),

  url: zod.string().url().or(zod.literal('')).nullable().optional(),

  imageId: zod
    .union([zod.string(), zod.promise(zod.string())])
    .nullable()
    .optional(),

  address: zod
    .object({
      formatted: zod.string(),
      name: zod.string().nullable().optional(),
      country: zod.string().nullable().optional(),
      countryCode: zod.string().nullable().optional(),
      region: zod.string().nullable().optional(),
      regionCode: zod.string().nullable().optional(),
      subRegion: zod.string().nullable().optional(),
      locality: zod.string().nullable().optional(),
      street: zod.string().nullable().optional(),
      postalCode: zod.string().nullable().optional(),
      houseNumber: zod.string().nullable().optional(),
      longitude: zod.number().nullable().optional(),
      latitude: zod.number().nullable().optional(),
      timeZone: zod.string().nullable().optional(),
      plusCode: zod.string().nullable().optional(),
      serviceId: zod.string().nullable().optional(),
      placeId: zod.string().nullable().optional(),
    })
    .nullable()
    .optional(),
})

const datesValidations = (
  data: {
    startDate: Date
    startTime: string
    endDate: Date | null
    endTime: string | null
    _includeEndDate: boolean
  },
  ctx: zod.RefinementCtx,
) => {
  const now = Date.now()
  const start = combineDateAndTime(data.startDate, data.startTime).getTime()

  // 1) start must be in the future
  if (isNaN(start) || start <= now) {
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      path: ['startDate'],
      message: 'Must be in the future',
    })
    ctx.addIssue({
      code: zod.ZodIssueCode.custom,
      path: ['startTime'],
      message: '',
    })
  }

  if (data._includeEndDate) {
    if (!data.endDate) {
      // 2) end date is required in case enabled
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ['endDate'],
        message: DATE_ERROR_MSG,
      })
    } else if (!data.endTime) {
      // 3) end time is required in case enabled
      ctx.addIssue({
        code: zod.ZodIssueCode.custom,
        path: ['endTime'],
        message: TIME_ERROR_MSG,
      })
    } else {
      // 4) end must be after start
      const end = combineDateAndTime(data.endDate, data.endTime).getTime()

      if (isNaN(end) || end <= start) {
        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['endDate'],
          message: 'End time must be later than start time',
        })

        ctx.addIssue({
          code: zod.ZodIssueCode.custom,
          path: ['endTime'],
          message: '',
        })
      }
    }
  }
}

export const eventCreateFormSchema = eventCommonFieldsSchema
  .extend({
    groupId: zod.string().optional(),
    status: zod.enum(statusOptions, {
      required_error: 'Status is required',
      invalid_type_error: 'Invalid status selected',
    }),
    privacy: zod.enum(EVENT_PRIVACY_KEYS, {
      required_error: 'Privacy setting is required',
      invalid_type_error: 'Invalid privacy choice',
    }),
    invitees: zod.array(zod.string()).optional(),
  })
  .superRefine(datesValidations)

export const eventEditFormSchema =
  eventCommonFieldsSchema.superRefine(datesValidations)

export type EventCommonFieldsFormType = zod.TypeOf<
  typeof eventCommonFieldsSchema
>
export type EventCreateFormType = zod.TypeOf<typeof eventCreateFormSchema>
export type EventEditFormType = zod.TypeOf<typeof eventEditFormSchema>

const commonDefaultValues: EventCommonFieldsFormType = {
  name: '',
  startDate: getStartOfNextDay(),
  startTime: '12:00',
  endDate: null,
  endTime: null,
  _includeEndDate: false,
  ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  url: '',
  description: '',
}

export const eventCreateFormDefaultValues: EventCreateFormType = {
  ...commonDefaultValues,
  status: 'CONFIRMED',
  privacy: 'PUBLIC',
  invitees: undefined,
}

export const eventEditFormDefaultValues: EventEditFormType = {
  ...commonDefaultValues,
}

export function getEditFormValues(
  event: Event,
  copyAsNew?: boolean,
): EventEditFormType | EventCreateFormType {
  const {
    privacy,
    name,
    startTime,
    endTime,
    ianaTimeZone,
    description,
    url,
    address,
    cover,
  } = event

  const startDate = new Date(startTime)
  const endDate = endTime ? new Date(endTime) : null

  return {
    ...(copyAsNew && {
      status: 'CONFIRMED',
      privacy: privacy as EventPrivacy,
      ...(privacy === 'DIRECT' && {
        invitees: [],
      }),
    }),
    name,
    startDate: startDate,
    startTime: getTimeString(startDate)!,
    endDate: endDate,
    endTime: getTimeString(endDate),
    _includeEndDate: !!endTime,
    ianaTimeZone:
      ianaTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    description,
    url,
    address,
    imageId: cover?.uploadId,
  }
}

export async function getFormSubmitData(
  data: EventCreateFormType | EventEditFormType,
) {
  try {
    const {
      startDate,
      startTime,
      endDate,
      endTime,
      imageId,
      description,
      _includeEndDate,
      ...rest
    } = data

    const resolvedImageId = await imageId

    const startISO = toISONoMs(combineDateAndTime(startDate, startTime))
    let endISO = null
    if (_includeEndDate) {
      endISO = toISONoMs(combineDateAndTime(endDate!, endTime!))
    }

    return {
      ...rest,
      imageId: resolvedImageId,
      startTime: startISO,
      ...(description && { description }),
      ...(endISO && { endTime: endISO }),
    }
  } catch (error) {
    logger.error('Failed to parse form data:', error)
    throw error
  }
}

export function getEditFormSubmitData(data: UpdateEvent): UpdateEvent {
  const { endTime, address, description, imageId, ...rest } = data

  return {
    ...rest,
    ...(endTime ? { endTime } : { removeEndTime: true }),
    ...(address ? { address } : { removeAddress: true }),
    ...(description ? { description } : { removeDescription: true }),
    ...(imageId ? { imageId } : { removeImage: true }),
  }
}
